
import { ReducerRegistry } from '../base/redux';

import {
    ADD_MESSAGE,
    CLEAR_MESSAGES,
    CLOSE_CHAT,
    CLOSE_IMAGE_PREVIEW,
    CLOSE_POLLS,
    DELETE_MESSAGE,
    DISMISS_FLOATING_CHAT_NOTIFICATION,
    EDIT_MESSAGE,
    OPEN_CHAT,
    OPEN_IMAGE_PREVIEW,
    OPEN_POLLS,
    RESTORE_MESSAGE,
    SET_CHAT_NOTIFICATIONS_HIDDEN,
    SET_EDITING_MESSAGE,
    SET_IS_POLL_TAB_FOCUSED,
    SET_PRIVATE_CHAT_DISABLED,
    SET_PRIVATE_MESSAGE_RECIPIENT,
    SET_VISITOR_TAB_FOCUSED,
    SHOW_FLOATING_CHAT_NOTIFICATION
} from './actionTypes';
import { MAX_CHAT_MESSAGES, MESSAGE_RESTORE_TIMEOUT } from './constants';


const initialChatNotificationsHidden = (() => {
    try {
        return window?.localStorage?.getItem('hide_chat_notifications') === 'true';
    } catch {
        return false;
    }
})();

const DEFAULT_STATE = {
    isOpen: false,
    isPollsTabFocused: false,
    isPollsOpen: false,

    // True when a non-visitor (main-room) participant is viewing the visitor
    // content panel (visitor chat or visitor polls) instead of the main-room
    // content panel.  Always ignored when iAmVisitor is true (visitors always
    // see their own visitor-scoped content).
    isVisitorTabFocused: false,

    lastReadMessage: undefined,
    lastReadPoll: undefined,
    messages: [],
    nbUnreadMessages: 0,
    privateMessageRecipient: undefined,
    editingMessage: undefined,
    imagePreview: {
        isOpen: false,
        imageUrl: null,
        fileName: null
    },
    isPrivateChatDisabled: false,
    deletedMessageIds: [],
    areChatNotificationsHidden: initialChatNotificationsHidden,
    floatingChatNotification: null,
    floatingChatNotifications: []
};

ReducerRegistry.register('features/chat', (state = DEFAULT_STATE, action) => {
    switch (action.type) {
    case ADD_MESSAGE: {
        const isDeleted = Boolean(action.deleteChat)
            || Boolean(action.messageId && state.deletedMessageIds?.includes(action.messageId));

        if (state.messages.some(m => (action.messageId && m.messageId === action.messageId)
            || (!action.messageId && m.message === action.message && m.id === action.id))) {
            return state;
        }

        const newMessage = {
            displayName: action.displayName,
            error: action.error,
            id: action.id,
            isReaction: action.isReaction,
            messageId: action.messageId,
            messageType: action.messageType,
            message: action.message,
            privateMessage: action.privateMessage,
            recipient: action.recipient,
            recipientId: action.recipientId,

            // Webinar role of the sender when the message was received:
            // 'moderator' | 'panelist' | undefined. Rendered as a badge next to
            // the sender name so the role survives the sender leaving the room.
            senderRole: action.senderRole,

            // Identifies the person behind the sending endpoint, so messages a
            // user sends from several tabs or devices group as one sender.
            senderIdentityKey: action.senderIdentityKey,

            // Origin of the message: 'visitor' for messages from the visitor (viewer) node,
            // 'main' for regular main-room chat. Missing source is treated as main-room.
            source: action.source === 'visitor' ? 'visitor' : 'main',

            // Chat thread the message belongs to: 'visitor' for the visitor chat thread (visitor
            // messages and main-room messages explicitly directed to visitors), 'main' otherwise.
            channel: action.channel === 'visitor' ? 'visitor' : 'main',

            // Stable per-session id of a visitor sender (their vnode occupant resource),
            // stamped by Prosody. Lets moderators target the visitor, e.g. chat suspension.
            vid: action.vid,
            timestamp: action.timestamp,
            deleteChat: isDeleted,
            deletedAt: action.deletedAt || (isDeleted ? Date.now() : undefined)
        };

        // React native, unlike web, needs a reverse sorted message list.
        // The buffer is capped at MAX_CHAT_MESSAGES so high-volume webinars can't grow it without
        // bound. The newest messages are always kept: on web (append order) drop from the front,
        // on native (prepend order) drop from the back. Older messages remain retrievable from the
        // XMPP archive (MAM) on scroll-up.
        const isNative = navigator.product === 'ReactNative';
        let messages = isNative
            ? [ newMessage, ...state.messages ]
            : [ ...state.messages, newMessage ];

        if (messages.length > MAX_CHAT_MESSAGES) {
            messages = isNative
                ? messages.slice(0, MAX_CHAT_MESSAGES)
                : messages.slice(messages.length - MAX_CHAT_MESSAGES);
        }

        return {
            ...state,
            lastReadMessage: action.hasRead ? newMessage : state.lastReadMessage,
            nbUnreadMessages: state.isPollsTabFocused
                ? state.nbUnreadMessages + 1
                : state.nbUnreadMessages,
            messages
        };
    }

    case CLEAR_MESSAGES:
        return {
            ...state,
            lastReadMessage: undefined,
            messages: []
        };

    case EDIT_MESSAGE: {
        let found = false;
        const newMessage = action.message;
        const messages = state.messages.map(m => {
            if (m.messageId === newMessage.messageId) {
                found = true;
                const isEdited = newMessage.isEdited === undefined
                    ? newMessage.message !== undefined && newMessage.message !== m.message
                    : Boolean(newMessage.isEdited);
                const updated = {
                    ...m,
                    ...newMessage,
                    isEdited
                };

                if (updated.newMessageId) {
                    updated.messageId = updated.newMessageId;
                    delete updated.newMessageId;
                }

                return updated;
            }

            return m;
        });

        // no change
        if (!found) {
            return state;
        }

        const updatedFloatingNotifications = (state.floatingChatNotifications || []).map(n => {
            if (n.messageId === newMessage.messageId) {
                return {
                    ...n,
                    message: newMessage.message
                };
            }

            return n;
        });

        return {
            ...state,
            messages,
            floatingChatNotification: state.floatingChatNotification?.messageId === newMessage.messageId
                ? {
                    ...state.floatingChatNotification,
                    message: newMessage.message
                }
                : state.floatingChatNotification,
            floatingChatNotifications: updatedFloatingNotifications
        };
    }

    case SET_PRIVATE_MESSAGE_RECIPIENT:
        return {
            ...state,
            privateMessageRecipient: action.participant
        };

    case SET_EDITING_MESSAGE:
        return {
            ...state,
            editingMessage: action.editingMessage
        };

    case OPEN_POLLS:
        return {
            ...state,
            isPollsOpen: true
        };
    case CLOSE_POLLS:
        return {
            ...state,
            isPollsOpen: false
        };

    case OPEN_CHAT:
        return {
            ...state,
            isOpen: true,
            isVisitorTabFocused: false,
            privateMessageRecipient: action.participant,
            nbUnreadMessages: state.isPollsTabFocused ? state.nbUnreadMessages : 0,
            floatingChatNotification: null,
            floatingChatNotifications: []
        };

    case DELETE_MESSAGE: {
        const messageId = action.messageId;
        const deletedAt = action.deletedAt || Date.now();
        const messages = state.messages.map(m => {
            if (m.messageId === messageId) {
                return {
                    ...m,
                    deleteChat: true,
                    deletedAt: m.deletedAt || deletedAt
                };
            }

            return m;
        });

        const deletedMessageIds = state.deletedMessageIds || [];
        const nextDeletedMessageIds = messageId && !deletedMessageIds.includes(messageId)
            ? [ ...deletedMessageIds, messageId ]
            : deletedMessageIds;

        const remainingNotifications = (state.floatingChatNotifications || []).filter(
            n => n.messageId !== messageId
        );

        return {
            ...state,
            messages,
            deletedMessageIds: nextDeletedMessageIds,
            floatingChatNotification: state.floatingChatNotification?.messageId === messageId
                ? remainingNotifications[remainingNotifications.length - 1] || null
                : state.floatingChatNotification,
            floatingChatNotifications: remainingNotifications
        };
    }

    case RESTORE_MESSAGE: {
        const messageId = action.messageId;
        const targetMessage = state.messages.find(m => m.messageId === messageId);
        const targetText = targetMessage?.message || '';
        const isFile = Boolean(targetText && (targetText.includes('class="file-attachment"') || targetText.includes('<a ')));

        if (isFile) {
            return state;
        }

        if (targetMessage?.deletedAt && (Date.now() - targetMessage.deletedAt > MESSAGE_RESTORE_TIMEOUT)) {
            return state;
        }

        const messages = state.messages.map(m => {
            if (m.messageId === messageId) {
                return {
                    ...m,
                    deleteChat: false,
                    deletedAt: undefined
                };
            }

            return m;
        });

        const deletedMessageIds = (state.deletedMessageIds || []).filter(
            id => id !== messageId
        );

        return {
            ...state,
            messages,
            deletedMessageIds
        };
    }

    case CLOSE_CHAT:
        return {
            ...state,
            isOpen: false,
            lastReadMessage:
          state.messages[
            navigator.product === 'ReactNative' ? 0 : state.messages.length - 1
          ],
            privateMessageRecipient: action.participant
        };

    case SET_IS_POLL_TAB_FOCUSED: {
        return {
            ...state,
            isPollsTabFocused: action.isPollsTabFocused,

            // Switching between chat/polls resets the visitor sub-panel so
            // the user lands on main-room content after every tab switch.
            isVisitorTabFocused: false
        };
    }
    case SET_VISITOR_TAB_FOCUSED: {
        return {
            ...state,
            isVisitorTabFocused: action.focused
        };
    }
    case SET_PRIVATE_CHAT_DISABLED:
        return {
            ...state,
            isPrivateChatDisabled: action.disabled,
            privateMessageRecipient: action.disabled ? undefined : state.privateMessageRecipient
        };

    case OPEN_IMAGE_PREVIEW: {
        return {
            ...state,
            imagePreview: {
                isOpen: true,
                imageUrl: action.imageUrl,
                fileName: action.fileName || null
            }
        };
    }

    case CLOSE_IMAGE_PREVIEW: {
        return {
            ...state,
            imagePreview: {
                isOpen: false,
                imageUrl: null,
                fileName: null
            }
        };
    }

    case SHOW_FLOATING_CHAT_NOTIFICATION: {
        if (state.isOpen || state.areChatNotificationsHidden) {
            return state;
        }

        const existingList = state.floatingChatNotifications || [];
        const filtered = existingList.filter(n => n.messageId !== action.payload.messageId);
        const updatedList = [ ...filtered, action.payload ].slice(-10);

        return {
            ...state,
            floatingChatNotification: action.payload,
            floatingChatNotifications: updatedList
        };
    }

    case DISMISS_FLOATING_CHAT_NOTIFICATION: {
        return {
            ...state,
            floatingChatNotification: null,
            floatingChatNotifications: []
        };
    }

    case SET_CHAT_NOTIFICATIONS_HIDDEN: {
        try {
            if (action.hidden) {
                window?.localStorage?.setItem('hide_chat_notifications', 'true');
            } else {
                window?.localStorage?.removeItem('hide_chat_notifications');
            }
        } catch {
            // Ignore localStorage errors
        }

        return {
            ...state,
            areChatNotificationsHidden: action.hidden,
            floatingChatNotification: action.hidden ? null : state.floatingChatNotification,
            floatingChatNotifications: action.hidden ? [] : state.floatingChatNotifications
        };
    }
    }

    return state;
});
